/**
 * useCurrentCase Hook
 *
 * Provides deterministic "current case" resolution for SRL users.
 * Implements Phase 1 requirement: track last active case per user.
 *
 * Priority:
 * 1. User's lastActiveCaseId from Firestore (if still accessible)
 * 2. Most recently updated accessible case
 * 3. null (triggers onboarding flow)
 *
 * @returns {
 *   currentCaseId: string | null,
 *   loading: boolean,
 *   error: Error | null
 * }
 */

import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuthState } from '@/auth/AuthProvider';
import { useCases } from './useCases';
import { clearCurrentCase } from '@/lib/updateCurrentCase';

interface CurrentCaseResult {
  currentCaseId: string | null;
  loading: boolean;
  error: Error | null;
}

export function useCurrentCase(): CurrentCaseResult {
  const { user } = useAuthState();
  const { data: cases, loading: casesLoading } = useCases();
  const [currentCaseId, setCurrentCaseId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function resolveCurrentCase() {
      if (!user?.uid) {
        setCurrentCaseId(null);
        setLoading(false);
        return;
      }

      // Wait for cases to load
      if (casesLoading) {
        return;
      }

      // If user has no cases, current case is null (triggers onboarding)
      if (!cases || cases.length === 0) {
        setCurrentCaseId(null);
        setLoading(false);
        return;
      }

      try {
        // Attempt to read lastActiveCaseId from Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const lastActiveCaseId = userData?.settings?.currentCaseId;

          // LAYER 1: VALIDATION - Validate that the case both exists AND is accessible to the user
          // Prevents redirect to non-existent or unauthorized cases (P0 new user blocker)
          if (lastActiveCaseId && cases.some((c: { id: string }) => c.id === lastActiveCaseId)) {
            console.log(`[useCurrentCase] ✅ Using validated stored case: ${lastActiveCaseId}`);
            setCurrentCaseId(lastActiveCaseId);
            setLoading(false);
            return;
          } else if (lastActiveCaseId) {
            // TELEMETRY: Track when we detect invalid pointers (should be prevented by Layer 3)
            console.error(`[useCurrentCase] ⚠️ INVALID POINTER DETECTED: Case ${lastActiveCaseId} not in accessible set`);
            console.error('[useCurrentCase] This indicates Layer 3 validation failed or case was deleted after being set');
            console.warn('[TELEMETRY] Invalid currentCaseId pointer detected - Layer 1 caught it');
            
            // PHASE 1+: SELF-HEAL - Clear the invalid pointer immediately
            // Fire-and-forget pattern: don't block UI routing if Firestore is slow
            clearCurrentCase(user.uid).then(() => {
              console.log(`[useCurrentCase] ✅ Invalid pointer cleared for user ${user.uid}`);
              console.warn('[TELEMETRY] Invalid currentCaseId pointer cleared - self-heal complete');
            }).catch((clearErr) => {
              console.error('[useCurrentCase] Failed to clear invalid pointer:', clearErr);
              console.error('[TELEMETRY] Invalid pointer clear failed - requires manual intervention');
            });
          }
        }

        // Fallback: Use most recently updated case
        // Cases are already sorted by updatedAt descending (from watchMyCases)
        const fallbackCase = cases[0] as { id: string };
        console.log(`[useCurrentCase] Using fallback case: ${fallbackCase.id}`);
        setCurrentCaseId(fallbackCase.id);
        setLoading(false);
      } catch (err) {
        console.error('[useCurrentCase] Error resolving current case:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));

        // Fallback to most recent case even on error
        const fallbackCase = cases[0] as { id: string };
        console.warn(`[useCurrentCase] Error occurred, using fallback case: ${fallbackCase.id}`);
        setCurrentCaseId(fallbackCase.id);
        setLoading(false);
      }
    }

    resolveCurrentCase();
  }, [user?.uid, cases, casesLoading]);

  return { currentCaseId, loading, error };
}
